import { json } from 'aurelia-fetch-client';
import { autoinject, computedFrom } from 'aurelia-framework';
import { ApiService } from '../../utils/api-service';
import {CostCenterServiceLink} from "../services/service-targetings/linked-mappings-service";

@autoinject
export class AccountingDimensionService {
	constructor(private api: ApiService) {
	}

	async getDimensionsForCustomer(customerId: string, getRemovableStatus: boolean = false): Promise<IAccountingDimensionAM[]> {
		return await this.api.fetchJson(`accounting-dimensions/for-customer/${customerId}${getRemovableStatus ? '?getRemovableStatus=true' : ''}`);
	}

	addDimension(customerId: string, dimension: IAccountingDimensionAM): Promise<IAccountingDimensionAM> {
		return this.api.fetchJson(`accounting-dimensions/${customerId}/add`, {
			method: 'post',
			body: json(dimension)
		});
	}

	updateDimension(dimension: IAccountingDimensionAM): Promise<Response> {
		return this.api.fetch(`accounting-dimensions/update`, {
			method: 'post',
			body: json(dimension)
		});
	}

	removeDimension(dimension: AccountingDimension) {
		return this.api.fetch(`accounting-dimensions/remove/${dimension.id}`, {
			method: 'post'
		});
	}

	addValue(dimensionId: string, value: AccountingDimensionValue): Promise<IAccountingDimensionValueAM> {
		return this.api.fetchJson(`accounting-dimensions/values/${dimensionId}/add`, {
			method: 'post',
			body: json(value)
		});
	}

	updateValue(value: AccountingDimensionValue) {
		return this.api.fetch(`accounting-dimensions/values/update`, {
			method: 'post',
			body: json(value)
		});
	}

	removeValue(value: AccountingDimensionValue) {
		return this.api.fetch(`accounting-dimensions/values/remove/${value.id}`, {
			method: 'post'
		});
	}

	importValues(dimensionId: string, values: IAccountingDimensionValueAM[]): Promise<IAccountingDimensionValueAM[]> {
		return this.api.fetchJson(`accounting-dimensions/values/${dimensionId}/import/`, {
			method: 'post',
			body: json(values)
		});
	}
}

export class AccountingDimension {
	static fromAM(dimensionAM: IAccountingDimensionAM) {
		return new AccountingDimension({
			id: dimensionAM.id,
			name: dimensionAM.name,
			values: dimensionAM.values ? dimensionAM.values.map(AccountingDimensionValue.fromAM) : []
		});
	}

	id: string;
	name: string;
	values: AccountingDimensionValue[] = [];
	valuesVisible = false;
	refresh: boolean;
	confirmRemove = false;
	update: (value: AccountingDimension) => Promise<Response>;

	constructor(init?: Partial<AccountingDimension>) {
		if (init != null) { Object.assign(this, init); }
	}

	toggleValues() {
		this.valuesVisible = !this.valuesVisible;
	}

	refreshValues() {
		this.refresh = true;
	}

	async changeName(value: string) {
		const oldName = this.name;
		this.name = value;

		const response = await this.update(this);
		if (response.status !== 200) {
			this.name = oldName;
		}
	}

	toAM(): IAccountingDimensionAM {
		return {
			id: this.id,
			name: this.name
		};
	}

	@computedFrom('values')
	get removable() {
		return this.values.length === 0;
	}
}

export class AccountingDimensionValue {
	static fromAM(valueAM: IAccountingDimensionValueAM) {
		return new AccountingDimensionValue(valueAM);
	}

	id: string;
	identifier: string;
	name: string;
	removable: boolean;
	confirmRemove = false;
	update: (value: AccountingDimensionValue) => void;

	constructor(init?: Partial<AccountingDimensionValue>) {
		if (init != null) { Object.assign(this, init); }
	}

	changeName(value: string) {
		this.name = value;
		this.update(this);
	}
	
	static areValuesEqual(values: string[], otherValues: string[]) : boolean {

		if ((!values || values.length == 0) && (!otherValues || otherValues.length == 0)) {
			return true; // both are empty
		}

		if (!values || !otherValues) { return false; }
		if (values.length != otherValues.length) { return false; }
		
		return values.every(v => otherValues.indexOf(v) >= 0);		
	}
}

export interface IAccountingDimensionAM {
	id?: string;
	name: string;
	values?: IAccountingDimensionValueAM[];
}

export interface IAddAccountingDimensionAM {
	name: string;
}

export interface IAddResultAccountingDimensionAM {
	id: string;
	name: string;
}

export interface IAccountingDimensionValueAM {
	id?: string;
	identifier: string;
	name: string;
	removable?: boolean;
}
